import { computed, readonly, ref } from 'vue';
import { apiUrl } from './api-url';

const debug = ref(false);
const simplified = ref(false);
const accessRole = ref<string | null>(null);
const sessionEmail = ref<string | null>(null);
const warehouseBranch = ref<'veracruz' | 'xalapa' | null>(null);
const pendingSurveysAvailable = ref(false);
const deliverySchedulesAvailable = ref(false);
const neo4j = ref(false);
const hideEmpty = ref(false);
const olderThanDays = ref(90);
const crm = ref(false);
const surveyEvidence = ref({ imageMaxBytes: 700 * 1024, imageMaxDimension: 1024, videoMaxBytes: 4 * 1024 * 1024, maxFiles: 8 });
const deliveryLogRules = ref({ timezone: 'America/Mexico_City', closingHours: { weekdays: '15:00', saturday: '12:00', sunday: null as null } });
let loaded = false;

export const debugMode = computed(() => debug.value && accessRole.value === 'administrator');
export const simplifiedMode = readonly(simplified);
export const sessionAccessRole = readonly(accessRole);
export const sessionWarehouseBranch = readonly(warehouseBranch);
export const sessionPendingSurveysAvailable = readonly(pendingSurveysAvailable);
export const sessionDeliverySchedulesAvailable = readonly(deliverySchedulesAvailable);
export const administratorSession = computed(() => accessRole.value === 'administrator');
export const operationalAdministratorSession = computed(() => administratorSession.value || accessRole.value === 'sistemas');
export const evidenceReaderSession = computed(() => operationalAdministratorSession.value || accessRole.value === 'gerente' || accessRole.value === 'collaborator' || Boolean(warehouseBranch.value));
export const debugRecordDeletionSession = computed(() => operationalAdministratorSession.value
  && sessionEmail.value?.trim().toLowerCase() === 'kzka90@gmail.com');
export const sessionResolved = computed(() => accessRole.value !== null);
export function setSessionAccess(
  role: string | null,
  branch: 'veracruz' | 'xalapa' | null = null,
  surveysAvailable = false,
  email: string | null = null,
  schedulesAvailable = false,
): void {
  accessRole.value = role;
  sessionEmail.value = email?.trim().toLowerCase() || null;
  warehouseBranch.value = branch;
  pendingSurveysAvailable.value = surveysAvailable;
  deliverySchedulesAvailable.value = schedulesAvailable;
}
export const neo4jEnabled = readonly(neo4j);
export const hideEmptyProjects = readonly(hideEmpty);
export const hideProjectsOlderThanDays = readonly(olderThanDays);
export const crmEnabled = readonly(crm);
export const surveyEvidenceLimits = readonly(surveyEvidence);
export function setSurveyEvidenceLimits(limits: { imageMaxBytes: number; imageMaxDimension: number; videoMaxBytes: number }): void {
  surveyEvidence.value = { ...limits, maxFiles: 8 };
}
export const deliveryClosingRules = readonly(deliveryLogRules);

export async function loadRuntimeConfig(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const response = await fetch(apiUrl('/api/runtime-config'), { signal: AbortSignal.timeout(5_000) });
    if (!response.ok) return;
    const value = await response.json() as {
      debug?: unknown;
      simplified?: unknown;
      neo4jEnabled?: unknown;
      hideEmptyProjects?: unknown;
      hideProjectsOlderThanDays?: unknown;
      crmEnabled?: unknown;
      surveyEvidence?: unknown;
      deliveryLogRules?: { timezone: string; closingHours: { weekdays: string; saturday: string; sunday: null } };
    };
    debug.value = value.debug === true;
    simplified.value = value.simplified === true;
    neo4j.value = value.neo4jEnabled === true;
    hideEmpty.value = value.hideEmptyProjects === true;
    if (typeof value.hideProjectsOlderThanDays === 'number' && value.hideProjectsOlderThanDays > 0) {
      olderThanDays.value = Math.floor(value.hideProjectsOlderThanDays);
    }
    crm.value = value.crmEnabled === true;
    if (value.deliveryLogRules) deliveryLogRules.value = value.deliveryLogRules;
    if (value.surveyEvidence && typeof value.surveyEvidence === 'object') {
      const evidence = value.surveyEvidence as Record<string, unknown>;
      surveyEvidence.value = {
        imageMaxBytes: typeof evidence.imageMaxBytes === 'number' ? evidence.imageMaxBytes : 700 * 1024,
        imageMaxDimension: typeof evidence.imageMaxDimension === 'number' ? evidence.imageMaxDimension : 1024,
        videoMaxBytes: typeof evidence.videoMaxBytes === 'number' ? evidence.videoMaxBytes : 4 * 1024 * 1024,
        maxFiles: typeof evidence.maxFiles === 'number' ? evidence.maxFiles : 8,
      };
    }
  } catch {
    debug.value = false;
    simplified.value = false;
    neo4j.value = false;
    hideEmpty.value = false;
    olderThanDays.value = 90;
    crm.value = false;
    surveyEvidence.value = { imageMaxBytes: 700 * 1024, imageMaxDimension: 1024, videoMaxBytes: 4 * 1024 * 1024, maxFiles: 8 };
  }
}
